import type {
  WorldFlythrough,
  WorldFlythroughChapter,
  WorldFlythroughCue,
  WorldFlythroughKeyframe,
} from '@viewer/world/worldTypes';

type Vec3 = [number, number, number];

export interface FlythroughSample {
  position: Vec3;
  rotation: Vec3;
  fov?: number;
}

export interface ResolvedFlythroughChapter {
  id: string;
  label: string;
  timeMs: number;
  progress: number;
}

interface TimedKeyframe {
  keyframe: WorldFlythroughKeyframe;
  timeMs: number;
}

const DEFAULT_SEGMENT_MS = 3_000;

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function vec3(value: Vec3 | undefined, fallback: Vec3): Vec3 {
  return value ? [value[0], value[1], value[2]] : fallback;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
}

function catmull(a: number, b: number, c: number, d: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
}

function interpolateVec3(points: Vec3[], index: number, t: number, spline: boolean): Vec3 {
  const a = points[Math.max(0, index - 1)]!;
  const b = points[index]!;
  const c = points[Math.min(points.length - 1, index + 1)]!;
  const d = points[Math.min(points.length - 1, index + 2)]!;
  if (!spline || points.length < 3) {
    return [lerp(b[0], c[0], t), lerp(b[1], c[1], t), lerp(b[2], c[2], t)];
  }
  return [
    catmull(a[0], b[0], c[0], d[0], t),
    catmull(a[1], b[1], c[1], d[1], t),
    catmull(a[2], b[2], c[2], d[2], t),
  ];
}

function normalizeKeyframes(tour: WorldFlythrough): TimedKeyframe[] {
  const durationMs = resolveFlythroughDurationMs(tour);
  const lastIndex = Math.max(1, tour.keyframes.length - 1);
  return tour.keyframes
    .map((keyframe, index) => {
      const explicitMs = finiteNumber(keyframe.timeMs)
        ? keyframe.timeMs
        : finiteNumber(keyframe.time)
          ? keyframe.time * 1000
          : (durationMs * index) / lastIndex;
      return {
        keyframe,
        timeMs: Math.max(0, Math.min(durationMs, explicitMs)),
      };
    })
    .sort((a, b) => a.timeMs - b.timeMs);
}

function segmentForTime(keys: TimedKeyframe[], elapsedMs: number): { index: number; t: number } {
  if (keys.length <= 1) return { index: 0, t: 0 };
  if (elapsedMs <= keys[0]!.timeMs) return { index: 0, t: 0 };
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i]!;
    const b = keys[i + 1]!;
    if (elapsedMs <= b.timeMs) {
      const span = Math.max(1, b.timeMs - a.timeMs);
      return { index: i, t: clamp01((elapsedMs - a.timeMs) / span) };
    }
  }
  return { index: keys.length - 2, t: 1 };
}

function targetForKeyframe(keyframe: WorldFlythroughKeyframe): Vec3 | undefined {
  return keyframe.target ?? keyframe.lookAt;
}

function interpolateAngleDeg(a: number, b: number, t: number): number {
  let delta = ((b - a + 540) % 360) - 180;
  if (delta === -180) delta = 180;
  return a + delta * t;
}

function interpolateRotation(keys: TimedKeyframe[], index: number, t: number): Vec3 {
  const a = keys[index]?.keyframe.rotation ?? [0, 0, 0];
  const b = keys[Math.min(keys.length - 1, index + 1)]?.keyframe.rotation ?? a;
  return [
    interpolateAngleDeg(a[0], b[0], t),
    interpolateAngleDeg(a[1], b[1], t),
    interpolateAngleDeg(a[2], b[2], t),
  ];
}

export function lookRotationDegrees(position: Vec3, target: Vec3): Vec3 {
  const dx = target[0] - position[0];
  const dy = target[1] - position[1];
  const dz = target[2] - position[2];
  const len = Math.max(1e-6, Math.hypot(dx, dy, dz));
  const yawRaw = (Math.atan2(-dx / len, -dz / len) * 180) / Math.PI;
  const yaw = Math.abs(yawRaw + 180) < 1e-9 ? 180 : yawRaw;
  const pitch = (Math.asin(dy / len) * 180) / Math.PI;
  return [pitch, yaw, 0];
}

export function resolveFlythroughDurationMs(tour: WorldFlythrough): number {
  if (finiteNumber(tour.durationMs) && tour.durationMs > 0) return tour.durationMs;
  let maxTimeMs = 0;
  for (const keyframe of tour.keyframes) {
    if (finiteNumber(keyframe.timeMs)) maxTimeMs = Math.max(maxTimeMs, keyframe.timeMs);
    else if (finiteNumber(keyframe.time)) maxTimeMs = Math.max(maxTimeMs, keyframe.time * 1000);
  }
  if (maxTimeMs > 0) return maxTimeMs;
  return Math.max(DEFAULT_SEGMENT_MS, Math.max(1, tour.keyframes.length - 1) * DEFAULT_SEGMENT_MS);
}

function flythroughTimelineTimeMs(
  tour: WorldFlythrough,
  item: Pick<WorldFlythroughCue | WorldFlythroughChapter, 'at' | 'time' | 'timeMs'>,
): number {
  if (finiteNumber(item.timeMs)) return Math.max(0, item.timeMs);
  if (finiteNumber(item.time)) return Math.max(0, item.time * 1000);
  if (finiteNumber(item.at)) return clamp01(item.at) * resolveFlythroughDurationMs(tour);
  return 0;
}

export function flythroughCueTimeMs(tour: WorldFlythrough, cue: WorldFlythroughCue): number {
  return flythroughTimelineTimeMs(tour, cue);
}

export function resolveFlythroughChapters(tour: WorldFlythrough): ResolvedFlythroughChapter[] {
  const durationMs = resolveFlythroughDurationMs(tour);
  return (tour.chapters ?? [])
    .map((chapter) => {
      const timeMs = Math.max(0, Math.min(durationMs, flythroughTimelineTimeMs(tour, chapter)));
      return {
        id: chapter.id,
        label: chapter.label,
        timeMs,
        progress: durationMs > 0 ? timeMs / durationMs : 0,
      };
    })
    .sort((a, b) => a.timeMs - b.timeMs);
}

export function collectDueFlythroughCues(
  tour: WorldFlythrough,
  previousMs: number,
  nextMs: number,
  fired: Set<string>,
): WorldFlythroughCue[] {
  const due: WorldFlythroughCue[] = [];
  for (let i = 0; i < (tour.cues ?? []).length; i++) {
    const cue = tour.cues![i]!;
    const cueMs = flythroughCueTimeMs(tour, cue);
    const key = `${i}:${cueMs}:${cue.action}`;
    if (fired.has(key)) continue;
    if (cueMs > previousMs && cueMs <= nextMs) {
      fired.add(key);
      due.push(cue);
    }
  }
  return due;
}

export function sampleFlythrough(tour: WorldFlythrough, elapsedMs: number): FlythroughSample {
  if (tour.keyframes.length === 0) {
    return { position: [0, 1.5, 0], rotation: [0, 0, 0] };
  }
  const durationMs = resolveFlythroughDurationMs(tour);
  const timeMs = Math.max(0, Math.min(durationMs, elapsedMs));
  const keys = normalizeKeyframes(tour);
  const { index, t } = segmentForTime(keys, timeMs);
  const eased = smoothstep(t);
  const spline = tour.interpolation !== 'linear' && tour.interpolation !== 'step';
  const positions = keys.map((item) => item.keyframe.position);
  const targets = keys.map((item) => targetForKeyframe(item.keyframe)).filter(Boolean) as Vec3[];

  const position =
    tour.interpolation === 'step'
      ? vec3(keys[index]?.keyframe.position, positions[0]!)
      : interpolateVec3(positions, index, eased, spline);

  let rotation: Vec3;
  if (targets.length === keys.length) {
    const target =
      tour.interpolation === 'step'
        ? targets[index]!
        : interpolateVec3(targets, index, eased, spline);
    rotation = lookRotationDegrees(position, target);
  } else if (keys[index]?.keyframe.rotation) {
    rotation = interpolateRotation(keys, index, eased);
  } else {
    const aheadIndex = Math.min(keys.length - 1, index + 1);
    const ahead = positions[aheadIndex] ?? positions[index]!;
    rotation = lookRotationDegrees(position, ahead);
  }

  const keyA = keys[index]?.keyframe;
  const keyB = keys[Math.min(keys.length - 1, index + 1)]?.keyframe ?? keyA;
  const fov =
    finiteNumber(keyA?.fov) && finiteNumber(keyB?.fov)
      ? lerp(keyA.fov, keyB.fov, eased)
      : (keyA?.fov ?? keyB?.fov);

  return { position, rotation, fov };
}

export function interpolateFlythroughSamples(
  from: FlythroughSample,
  to: FlythroughSample,
  t: number,
): FlythroughSample {
  const eased = smoothstep(clamp01(t));
  const fov =
    finiteNumber(from.fov) && finiteNumber(to.fov)
      ? lerp(from.fov, to.fov, eased)
      : (to.fov ?? from.fov);
  return {
    position: lerpVec3(from.position, to.position, eased),
    rotation: [
      interpolateAngleDeg(from.rotation[0], to.rotation[0], eased),
      interpolateAngleDeg(from.rotation[1], to.rotation[1], eased),
      interpolateAngleDeg(from.rotation[2], to.rotation[2], eased),
    ],
    fov,
  };
}

export function sampleFlythroughResume(
  tour: WorldFlythrough,
  from: FlythroughSample,
  targetElapsedMs: number,
  blendElapsedMs: number,
  blendDurationMs: number,
): FlythroughSample {
  const target = sampleFlythrough(tour, targetElapsedMs);
  const t = blendDurationMs <= 0 ? 1 : blendElapsedMs / blendDurationMs;
  return interpolateFlythroughSamples(from, target, t);
}
